import { useEffect, useState } from 'react';
import type { PublicStream } from '@zip2live/shared';
import { Avatar, Header, Link, LiveBadge } from '../components/Brand.tsx';
import { FollowButton } from '../components/FollowButton.tsx';
import { LinkIcon, LockIcon, ShareIcon } from '../components/Icons.tsx';
import { count } from '../components/StreamStats.tsx';
import { navigate } from '../lib/router.ts';
import { api, type ChannelPage } from '../lib/api.ts';
import { shareLink } from '../lib/share.ts';
import { Watch } from './Watch.tsx';

/**
 * zip2live.com/:handle → the creator's live stream while they're live (that's the link creators
 * share), otherwise their channel: photo, name, bio, links and past streams. An open offline page
 * switches to the stream when it starts.
 *
 * zip2live.com/:handle/about → always the channel, live or not (the creator strip under a stream
 * links here). When they're live it says so, with a button to the stream.
 */
export function Channel({ handle, about = false }: { handle: string; about?: boolean }) {
  const [page, setPage] = useState<'loading' | 'missing' | ChannelPage>('loading');
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let alive = true;
    const load = () =>
      api.channel(handle).then(
        (c) => {
          if (!alive) return;
          setPage((prev) => (typeof prev === 'object' && prev.handle === c.handle ? { ...c, replays: refreshed(prev.replays, c.replays) } : c));
          // Check again shortly, so an open page notices the creator going live (or ending).
          if (!c.live || about) timer = setTimeout(load, 15_000);
        },
        () => alive && setPage((s) => (s === 'loading' ? 'missing' : s)),
      );
    void load();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [handle, about]);

  if (typeof page === 'object' && page.live && !about) return <Watch id={page.live.id} />;
  return (
    <main className="flex min-h-full flex-col bg-navy-900 text-white">
      <Header tone="dark" />
      {page === 'loading' && <p className="mx-auto mt-24 font-semibold text-white/70">Loading…</p>}
      {page === 'missing' && (
        <div className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center px-6 pb-20 text-center">
          <p className="speed-text text-4xl">Page not found</p>
          <p className="mt-3 text-muted-dark">That link isn’t anyone’s yet. It could be yours.</p>
          <button type="button" onClick={() => navigate('/go-live')} className="btn-race mt-10 h-15 px-9 text-xl">
            Go live yourself
          </button>
        </div>
      )}
      {typeof page === 'object' && <Profile page={page} about={about} onMore={(more) => setPage({ ...page, replays: more })} />}
    </main>
  );
}

/**
 * A refresh brings the newest page of past streams (a stream that just ended appears on an open
 * page); pages already loaded with Show more stay below it.
 */
function refreshed(prev: ChannelPage['replays'], fresh: ChannelPage['replays']): ChannelPage['replays'] {
  if (prev.items.length <= fresh.items.length) return fresh;
  const seen = new Set(fresh.items.map((s) => s.id));
  return { items: [...fresh.items, ...prev.items.filter((s) => !seen.has(s.id))], nextCursor: prev.nextCursor };
}

function Profile({ page, about, onMore }: { page: ChannelPage; about: boolean; onMore: (replays: ChannelPage['replays']) => void }) {
  const name = page.displayName ?? `@${page.handle}`;
  const url = `${location.origin}/${page.handle}`;
  return (
    <div className="mx-auto w-full max-w-6xl px-4 pt-2 pb-20 sm:px-8 sm:pt-6">
      <section className="rounded-3xl bg-navy px-5 py-6 sm:px-9 sm:py-9">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-8">
          <Avatar name={name} photoUrl={page.photoUrl} className="h-24 w-24 border-4 border-line-dark text-4xl sm:h-32 sm:w-32 sm:text-6xl" />
          <div className="min-w-0 flex-1">
            <h1 className="speed-text text-[clamp(2rem,7vw,3.25rem)] leading-[0.95] [overflow-wrap:anywhere]">{name}</h1>
            <p className="mt-2 font-mono text-[15px] text-faint-dark">@{page.handle}</p>
            {page.bio && <p className="mt-4 max-w-2xl leading-relaxed whitespace-pre-line text-muted-dark [overflow-wrap:anywhere]">{page.bio}</p>}
            {page.links.length > 0 && (
              <ul className="mt-4 flex flex-wrap gap-2" aria-label="Links">
                {page.links.map((l) => (
                  <li key={l.url} className="min-w-0">
                    <a
                      href={l.url}
                      target="_blank"
                      rel="nofollow ugc noopener noreferrer"
                      className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-white/10 px-3.5 py-2 text-sm font-bold hover:bg-white/20"
                    >
                      <LinkIcon />
                      <span className="truncate">{l.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2.5 sm:flex-col sm:items-end">
            {!page.owner && (
              <FollowButton target={{ handle: page.handle }} name={name} initialFollowers={page.followers} />
            )}
            {page.owner && (
              <Link to="/account#channel" className="inline-flex h-11 items-center rounded-xl bg-white px-4 text-[15px] font-extrabold text-navy hover:bg-mist">
                Edit channel
              </Link>
            )}
            <button
              type="button"
              onClick={() => void shareLink(url)}
              className="inline-flex h-11 items-center gap-2 rounded-xl border-2 border-line-dark px-4 text-[15px] font-extrabold hover:bg-white/10"
            >
              <ShareIcon size={18} />
              Share
            </button>
          </div>
        </div>

        <div className="mt-6 border-t border-white/10 pt-5">
          {page.live ? (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <p className="flex items-center gap-3 text-[17px] font-extrabold">
                <LiveBadge />
                {page.owner ? 'You’re live now' : `${name} is live now`}
              </p>
              <Link to={`/s/${page.live.id}`} className="btn-race shadow-race-deep h-12 w-full px-7 text-lg sm:w-auto">
                Watch
              </Link>
            </div>
          ) : (
            <p className="flex flex-wrap items-center gap-x-3 gap-y-2 text-muted-dark">
              <span className="rounded-full bg-navy-800 px-3.5 py-1.5 text-sm font-bold">Offline right now</span>
              <span className="text-sm">{about ? 'This page shows when they go live.' : 'Keep this page open: when they go live, the stream starts here.'}</span>
            </p>
          )}
        </div>
      </section>

      <PastStreams page={page} onMore={onMore} />

      {!page.owner && (
        <div className="mt-14 flex flex-wrap items-center justify-between gap-5 rounded-3xl bg-navy p-6 sm:p-7">
          <div>
            <p className="speed-text text-2xl sm:text-[28px]">Your turn.</p>
            <p className="mt-1 text-muted-dark">Go live from your phone or browser and get your own link in seconds. No signup.</p>
          </div>
          <button type="button" onClick={() => navigate('/go-live')} className="btn-race shadow-race-deep h-14 w-full px-8 text-xl sm:w-auto">
            GO LIVE
          </button>
        </div>
      )}
    </div>
  );
}

function PastStreams({ page, onMore }: { page: ChannelPage; onMore: (replays: ChannelPage['replays']) => void }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const { items, nextCursor } = page.replays;
  const more = async () => {
    if (!nextCursor) return;
    setBusy(true);
    setFailed(false);
    try {
      const next = await api.channelReplays(page.handle, nextCursor);
      const seen = new Set(items.map((s) => s.id));
      onMore({ items: [...items, ...next.items.filter((s) => !seen.has(s.id))], nextCursor: next.nextCursor });
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="mt-12" aria-labelledby="past-streams">
      <h2 id="past-streams" className="speed-text text-[28px] sm:text-[32px]">Past streams</h2>
      {items.length === 0 ? (
        <p className="mt-4 rounded-3xl bg-navy p-6 text-muted-dark">
          {page.owner ? 'Your replays show up here when your streams end.' : 'No replays to watch right now.'}
        </p>
      ) : (
        <ul className="mt-5 grid gap-x-5 gap-y-7 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((s) => (
            <li key={s.id}>
              <ReplayCard stream={s} />
            </li>
          ))}
        </ul>
      )}
      {nextCursor && (
        <div className="mt-8 flex flex-col items-center gap-2">
          <button type="button" disabled={busy} onClick={() => void more()} className="h-12 rounded-xl border-2 border-line-dark px-6 font-extrabold hover:bg-white/10 disabled:opacity-60">
            {busy ? 'Loading…' : 'Show more'}
          </button>
          {failed && <p role="alert" className="text-sm font-bold text-race-300">Couldn’t load more. Try again.</p>}
        </div>
      )}
    </section>
  );
}

/** "1:02:03" / "12:34" for the corner of a thumbnail. */
function clock(startedAt: number | null, endedAt: number | null): string | null {
  if (!startedAt || !endedAt || endedAt <= startedAt) return null;
  const total = Math.round((endedAt - startedAt) / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = String(total % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

function day(at: number, opts: Intl.DateTimeFormatOptions = {}) {
  const sameYear = new Date(at).getFullYear() === new Date().getFullYear();
  return new Date(at).toLocaleDateString([], { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }), ...opts });
}

function ReplayCard({ stream: s }: { stream: PublicStream }) {
  // The stream's latest preview picture; a branded card when there is none.
  const thumbnail = s.thumbnailUrl;
  const at = s.startedAt ?? s.createdAt;
  const length = clock(s.startedAt, s.endedAt);
  return (
    <Link to={`/s/${s.id}`} className="group block">
      <div className="relative aspect-video overflow-hidden rounded-2xl bg-navy">
        {thumbnail ? (
          <img src={thumbnail} alt="" loading="lazy" className="h-full w-full object-cover transition group-hover:scale-[1.03]" />
        ) : (
          <ReplayPlaceholder />
        )}
        {s.private && (
          <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 text-xs font-extrabold text-navy">
            <LockIcon size={12} /> Private
          </span>
        )}
        {length && <span className="absolute right-2.5 bottom-2.5 rounded-md bg-black/75 px-1.5 py-0.5 text-xs font-bold tabular-nums">{length}</span>}
      </div>
      <p className="mt-3 line-clamp-2 font-extrabold [overflow-wrap:anywhere] group-hover:text-race-300">
        {s.title ?? `Stream on ${day(at, { weekday: 'short' })}, ${new Date(at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}
      </p>
      <p className="mt-1 text-sm text-muted-dark">
        {day(at)} · {count(s.views)} {s.views === 1 ? 'view' : 'views'}
        {s.private && ' · only you can see this'}
      </p>
    </Link>
  );
}

/** Stand-in thumbnail: the brand's twin speed stripes and a play mark on flat navy. */
function ReplayPlaceholder() {
  return (
    <div className="flex h-full w-full items-center justify-center gap-4 bg-navy transition group-hover:bg-navy-700" aria-hidden>
      <span className="flex flex-col gap-1.5">
        <span className="h-1.5 w-12 -skew-x-[24deg] bg-white/85" />
        <span className="ml-3.5 h-1.5 w-8 -skew-x-[24deg] bg-race" />
      </span>
      <span className="flex h-14 w-14 items-center justify-center rounded-full border-[3px] border-white/85">
        <svg width="22" height="22" viewBox="0 0 24 24" className="ml-1 text-race" fill="currentColor"><path d="M7 4.5v15l12.5-7.5z" /></svg>
      </span>
    </div>
  );
}
